// Shared engine for the public tech-stack quizzes (/<slug>-quiz).
//
// Each quiz owns a bank of 100 questions (30 easy, 40 average, 30 very hard).
// A run draws a random 10 per tier (30 total), shown easiest → hardest, with the
// four answer choices shuffled per question. Practice only: no points.

export type QuizDifficulty = 'easy' | 'medium' | 'hard';
export type ChoiceKey = 'a' | 'b' | 'c' | 'd';

export interface QuizQuestion {
    id: number;
    difficulty: QuizDifficulty;
    question: string;
    choices: Record<ChoiceKey, string>;
    answer: ChoiceKey;
    explanation: string;
}

export const DIFFICULTY_LABELS: Record<QuizDifficulty, string> = {
    easy: 'Easy',
    medium: 'Average',
    hard: 'Very Hard',
};

export const DIFFICULTY_ORDER: QuizDifficulty[] = ['easy', 'medium', 'hard'];

/** Questions drawn from each tier for one run. */
export const QUESTIONS_PER_DIFFICULTY = 10;

/** Total questions served in one run. */
export const QUIZ_TOTAL = QUESTIONS_PER_DIFFICULTY * DIFFICULTY_ORDER.length;

/**
 * Time budget. 20 minutes = 40 seconds/question on average. These are
 * multiple-choice recognition questions, not problems you work out, so a
 * knowledgeable taker needs ~15 min; this leaves margin without making it a
 * take-home. The quiz is always timed: this is the only mode.
 */
export const QUIZ_DURATION_MIN = 20;
export const QUIZ_DURATION_MS = QUIZ_DURATION_MIN * 60 * 1000;

/** Milliseconds -> "M:SS" (or "MM:SS"). */
export const formatClock = (ms: number) => {
    const totalSec = Math.max(0, Math.floor(ms / 1000));
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
};

/** Headline label shown next to a percentage score. */
export const gradeFor = (pct: number) =>
    pct >= 90 ? 'Elite' : pct >= 75 ? 'Strong' : pct >= 50 ? 'Solid start' : 'Keep training';

export interface QuizOption {
    /** Display key after shuffling: 'a' | 'b' | 'c' | 'd'. */
    key: ChoiceKey;
    text: string;
}

export interface PreparedQuestion {
    id: number;
    difficulty: QuizDifficulty;
    question: string;
    /** Options in shuffled display order, re-lettered a..d. */
    options: QuizOption[];
    /** The display key that is correct after shuffling. */
    correctKey: ChoiceKey;
    explanation: string;
}

const LETTERS: ChoiceKey[] = ['a', 'b', 'c', 'd'];

function shuffle<T>(input: readonly T[]): T[] {
    const arr = input.slice();
    for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
}

function prepare(q: QuizQuestion): PreparedQuestion {
    const shuffledEntries = shuffle(LETTERS.map((k) => ({ originalKey: k, text: q.choices[k] })));
    let correctKey: ChoiceKey = 'a';
    const options: QuizOption[] = shuffledEntries.map((entry, i) => {
        const key = LETTERS[i];
        if (entry.originalKey === q.answer) correctKey = key;
        return { key, text: entry.text };
    });
    return {
        id: q.id,
        difficulty: q.difficulty,
        question: q.question,
        options,
        correctKey,
        explanation: q.explanation,
    };
}

/**
 * Draw a fresh run from a bank: QUESTIONS_PER_DIFFICULTY random questions per tier,
 * presented in ascending difficulty. Only the within-tier selection and the
 * answer-choice order are randomized.
 */
export function buildQuiz(bank: QuizQuestion[]): PreparedQuestion[] {
    const drawTier = (d: QuizDifficulty) =>
        shuffle(bank.filter((q) => q.difficulty === d)).slice(0, QUESTIONS_PER_DIFFICULTY);

    return DIFFICULTY_ORDER.flatMap(drawTier).map(prepare);
}
